import { Helmet } from 'react-helmet-async';
import { APP_VERSION, getEnvironmentInfo, versionUtils } from '../../config/version';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { 
  Info, 
  Download, 
  RefreshCw, 
  Calendar, 
  GitBranch, 
  Settings,
  CheckCircle,
  AlertCircle
} from 'lucide-react';
import { HelpPageLayout } from '../../components/help/HelpPageLayout';

const Version: React.FC = () => {
  const handleRefresh = () => {
    window.location.reload();
  };

  const isNewVersion = versionUtils.isNewVersion();

  return (
    <HelpPageLayout>
      <Helmet>
        <title>Version Information - Spinner Games !!</title>
        <meta name="description" content="Current version information and update details for Spinner Games !!" />
      </Helmet>

      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
          Version Information
        </h1>
        <p className="text-gray-600 dark:text-gray-300">
          Current version details and update information
        </p>
      </div>

      {/* Current Version Card */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Info className="w-5 h-5" />
            Current Version
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600 dark:text-gray-300">
                  Version:
                </span>
                <span className="text-sm font-mono bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 px-2 py-1 rounded">
                  v{APP_VERSION.version}
                </span>
              </div>
              
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600 dark:text-gray-300">
                  Environment:
                </span>
                <span className={`text-sm font-mono px-2 py-1 rounded ${
                  APP_VERSION.environment === 'production' 
                    ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200' 
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200'
                }`}>
                  {APP_VERSION.environment}
                </span>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600 dark:text-gray-300">
                  Build:
                </span>
                <span className="text-sm font-mono text-gray-900 dark:text-white">
                  {APP_VERSION.buildNumber}
                </span>
              </div>
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600 dark:text-gray-300">
                  Build Date:
                </span>
                <span className="text-sm text-gray-900 dark:text-white">
                  {new Date(APP_VERSION.buildDate).toLocaleDateString()}
                </span>
              </div>

              {APP_VERSION.commitHash && APP_VERSION.commitHash !== 'unknown' && (
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-300">
                    Commit:
                  </span>
                  <span className="text-sm font-mono text-gray-900 dark:text-white">
                    {APP_VERSION.commitHash.substring(0, 8)}
                  </span>
                </div>
              )}

              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600 dark:text-gray-300">
                  Status:
                </span>
                <div className="flex items-center gap-1">
                  {isNewVersion ? (
                    <>
                      <CheckCircle className="w-4 h-4 text-green-500" />
                      <span className="text-sm text-green-600 dark:text-green-400">
                        New Version
                      </span>
                    </>
                  ) : (
                    <>
                      <AlertCircle className="w-4 h-4 text-blue-500" />
                      <span className="text-sm text-blue-600 dark:text-blue-400">
                        Up to Date
                      </span>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Features Card */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings className="w-5 h-5" />
            Features
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {APP_VERSION.features.map((feature, index) => (
              <div key={index} className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                <span className="text-sm text-gray-700 dark:text-gray-300">
                  {feature}
                </span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>



      {/* Actions */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <RefreshCw className="w-5 h-5" />
            Actions
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-3">
            <Button
              onClick={handleRefresh}
              className="flex items-center gap-2"
            >
              <Download className="w-4 h-4" />
              Refresh App
            </Button>
            
            <Button
              onClick={() => window.open('/help', '_self')}
              className="flex items-center gap-2 border border-gray-300 hover:bg-gray-50 dark:border-gray-600 dark:hover:bg-gray-700"
            >
              <Info className="w-4 h-4" />
              Back to Help
            </Button>
          </div>
        </CardContent>
      </Card>
        </div>
    </HelpPageLayout>
  );
};

export default Version; 